/**
 * TEMP-H-009: Billing Event Card Component
 * 
 * Specialized renderer for billing committed events showing USD costs,
 * operations summary, provider details, and cost breakdowns.
 */

import React, { memo } from 'react';
import { 
  DollarSign, 
  CreditCard, 
  Clock, 
  Hash,
  Zap,
  Database,
  ExternalLink,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import type { TimelineEvent } from '@/types/execution-events';

interface BillingEventCardProps {
  event: TimelineEvent;
  compact?: boolean;
}

interface CostBreakdown {
  provider: string;
  model?: string;
  costUsd: number;
  tokensUsed?: number;
  operations?: number;
  type?: 'generation' | 'qc' | 'processing' | 'storage';
}

const CostBreakdownItem: React.FC<{ breakdown: CostBreakdown }> = ({ breakdown }) => {
  const getProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'openai': return '🤖';
      case 'anthropic': return '🧠';
      case 'google': return '🔍';
      case 'elevenlabs': return '🗣️';
      case 'runway': return '🎬';
      case 'midjourney': return '🎨';
      case 'aws': return '☁️';
      default: return '⚡';
    }
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'generation': return 'text-blue-300';
      case 'qc': return 'text-purple-300';
      case 'processing': return 'text-green-300';
      case 'storage': return 'text-orange-300';
      default: return 'text-gray-300';
    }
  };

  const formatTokens = (tokens?: number) => {
    if (!tokens) return null;
    if (tokens < 1000) return `${tokens} tokens`;
    if (tokens < 1000000) return `${(tokens / 1000).toFixed(1)}K tokens`;
    return `${(tokens / 1000000).toFixed(1)}M tokens`;
  };

  return (
    <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
      <div className="flex items-center gap-3">
        <span className="text-lg">{getProviderIcon(breakdown.provider)}</span>
        <div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-white">{breakdown.provider}</span>
            {breakdown.model && (
              <span className="text-xs text-gray-400">{breakdown.model}</span>
            )}
            {breakdown.type && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${getTypeColor(breakdown.type)}`}>
                {breakdown.type}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
            {formatTokens(breakdown.tokensUsed) && (
              <span>{formatTokens(breakdown.tokensUsed)}</span>
            )}
            {breakdown.operations && (
              <span>{breakdown.operations.toLocaleString()} ops</span>
            )}
          </div>
        </div>
      </div>
      
      <div className="text-right">
        <div className="text-sm font-mono text-green-400">
          ${breakdown.costUsd.toFixed(4)}
        </div>
      </div>
    </div>
  );
};

export const BillingEventCard = memo(function BillingEventCard({
  event,
  compact = false
}: BillingEventCardProps) {
  // Extract billing data from event payload
  const billingPayload = event.payload || {};
  const totalCostUsd = billingPayload.costUsd || billingPayload.totalCostUsd || 0;
  const provider = billingPayload.provider;
  const model = billingPayload.model;
  const tokensUsed = billingPayload.tokensUsed || billingPayload.tokens_used;
  const operations = billingPayload.operations || billingPayload.operationsCount;
  const runId = billingPayload.run_id || billingPayload.runId;
  const committedAt = billingPayload.committed_at || billingPayload.committedAt || event.timestamp;
  const costBreakdown = billingPayload.costBreakdown || billingPayload.cost_breakdown;

  // Calculate operations per dollar for efficiency metric
  const operationsPerDollar = operations && totalCostUsd > 0 ? operations / totalCostUsd : null;

  // Format cost with appropriate precision
  const formatCost = (cost: number) => {
    if (cost >= 1) return `$${cost.toFixed(2)}`;
    if (cost >= 0.01) return `$${cost.toFixed(3)}`;
    if (cost >= 0.001) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(4)}`;
  };

  // Format tokens
  const formatTokens = (tokens?: number) => {
    if (!tokens) return null;
    if (tokens < 1000) return `${tokens}`;
    if (tokens < 1000000) return `${(tokens / 1000).toFixed(1)}K`;
    return `${(tokens / 1000000).toFixed(1)}M`;
  };

  // Determine if this is a high cost event (subjective threshold)
  const isHighCost = totalCostUsd > 1.0;
  const isMediumCost = totalCostUsd > 0.1;

  return (
    <div className={`border-l-2 ${isHighCost ? 'border-l-orange-500' : 'border-l-green-500'} bg-gray-900/50`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="mt-1 p-2 bg-green-500/20 rounded-lg">
            <DollarSign className="w-4 h-4 text-green-400" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-white">
                  {event.displayTitle}
                </h4>
                {event.displayDescription && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {event.displayDescription}
                  </p>
                )}
              </div>
              
              {/* Total Cost */}
              <div className="text-right">
                <div className={`text-xl font-mono ${
                  isHighCost ? 'text-orange-400' : isMediumCost ? 'text-yellow-400' : 'text-green-400'
                }`}>
                  {formatCost(totalCostUsd)}
                </div>
                <div className="text-xs text-gray-500">Total Cost</div>
              </div>
            </div>

            {/* Provider and Model */}
            <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
              {provider && (
                <div className="flex items-center gap-1">
                  <CreditCard className="w-3 h-3" />
                  <span className="font-medium text-blue-300">{provider}</span>
                  {model && <span className="text-gray-500">({model})</span>}
                </div>
              )}
              
              {runId && (
                <div className="flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  <span className="font-mono">{runId.slice(0, 8)}</span>
                </div>
              )}
              
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{new Date(committedAt).toLocaleTimeString()}</span>
              </div>
            </div>

            {/* Usage Summary */}
            <div className="grid grid-cols-2 gap-4 mb-3">
              {tokensUsed && (
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-blue-400" />
                  <div>
                    <div className="text-sm font-mono text-white">{formatTokens(tokensUsed)}</div>
                    <div className="text-xs text-gray-500">Tokens Used</div>
                  </div>
                </div>
              )}
              
              {operations && (
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <div>
                    <div className="text-sm font-mono text-white">{operations.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Operations</div>
                  </div>
                </div>
              )}
            </div>

            {/* Efficiency Metric */}
            {operationsPerDollar && !compact && (
              <div className="flex items-center gap-2 p-2 bg-blue-500/10 rounded-lg mb-3">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <div className="text-xs">
                  <span className="text-blue-300 font-medium">
                    {Math.round(operationsPerDollar).toLocaleString()} operations per dollar
                  </span>
                  <span className="text-gray-400 ml-2">Efficiency metric</span>
                </div>
              </div>
            )}

            {/* Cost Breakdown */}
            {!compact && costBreakdown && Array.isArray(costBreakdown) && costBreakdown.length > 0 && (
              <div className="space-y-3">
                <h5 className="text-xs font-medium text-gray-400 flex items-center gap-2">
                  <CreditCard className="w-3 h-3" />
                  Cost Breakdown ({costBreakdown.length} items)
                </h5>
                
                <div className="space-y-2">
                  {costBreakdown.map((breakdown: CostBreakdown, index: number) => (
                    <CostBreakdownItem key={index} breakdown={breakdown} />
                  ))}
                </div>
              </div>
            )}

            {/* High Cost Warning */}
            {isHighCost && (
              <div className="mt-3 p-2 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs">
                    <div className="text-orange-300 font-medium mb-1">High Cost Event</div>
                    <div className="text-orange-400">
                      This operation incurred significant costs. Consider reviewing usage patterns.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Links */}
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="flex items-center gap-4 text-xs">
                <button className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors">
                  <ExternalLink className="w-3 h-3" />
                  View Billing Details
                </button>
                
                {provider && (
                  <button className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors">
                    <Database className="w-3 h-3" />
                    {provider} Usage Dashboard
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BillingEventCard;